/** @jsxImportSource @emotion/react */
import {
  List,
  useTheme,
  ListItem,
  Modal,
  Typography,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { QBLoader } from 'components';
import {
  linkPaymentToReport,
  removePaymentLinkToReport,
  searchSettlementPayments,
} from 'tabs/Accounting/api';
import { CreatePayment } from 'tabs/Accounting/Payments/CreatePayment';
import { SettlementPaymentDetails } from 'tabs/Accounting/types';

interface Props {
  reportId: string;
  merchantId: string;
  refresh: () => void;
  payments: SettlementPaymentDetails[];
  disabled?: boolean;
}

export const Payments = ({
  reportId,
  merchantId,
  refresh,
  payments,
  disabled,
}: Props) => {
  const { sizing, spacing } = useTheme();
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showCreatePaymentForm, setShowCreatePaymentForm] = useState(false);
  const [{ value: availablePayments, loading }, refreshPayments] =
    useAsyncFn(async () => {
      const paymentsResponse = await searchSettlementPayments({
        filters: { merchantId },
      });
      return paymentsResponse.payments.filter((p) => p.reportId === null);
    }, [payments]);

  useEffect(() => {
    void refreshPayments();
  }, [refreshPayments]);

  const [{ loading: linkLoading }, linkPayment] = useAsyncFn(
    async (paymentId: string) => {
      await linkPaymentToReport(reportId, paymentId);
      refresh();
    },
    [reportId]
  );

  const [{ loading: removeLinkLoading }, removeLinkPayment] = useAsyncFn(
    async (paymentId: string) => {
      await removePaymentLinkToReport(reportId, paymentId);
      refresh();
    },
    [reportId]
  );

  return (
    <>
      <Typography variant="subtitle">Payments</Typography>
      <List>
        {payments.map((p) => (
          <ListItem
            key={p.uuid}
            title={`Created: ${p.createdAt.toLocaleDateString()} ${p.createdAt.toLocaleTimeString()} - Paid: ${p.paidAt.toLocaleDateString()} ${p.paidAt.toLocaleTimeString()}`}
            subtitle={p.reference}
            content={p.euroAmount.toString() + '€'}
            subcontent={
              p.usdtAmount
                ? p.usdtAmount.toString() + ' USDT'
                : p.usdcAmount
                ? p.usdcAmount.toString() + ' USDC'
                : ''
            }
            action={
              <>
                {!disabled && (
                  <UtilityButton
                    icon="trash"
                    variant="icon"
                    onClick={() => removeLinkPayment(p.uuid)}
                    loading={removeLinkLoading}
                    disabled={removeLinkLoading}
                  />
                )}
              </>
            }
          />
        ))}
      </List>
      {!disabled && (
        <>
          {loading && <QBLoader />}
          {availablePayments && (
            <UtilityButton
              text="Add payment to report"
              icon="plus"
              onClick={() => setShowAddPaymentModal(true)}
            />
          )}
          {showAddPaymentModal && (
            <Modal
              title={showCreatePaymentForm ? undefined : 'Available payments'}
              closeModal={() => setShowAddPaymentModal(false)}
            >
              {showCreatePaymentForm ? (
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing.xxs,
                    maxHeight: '80vh',
                    width: '50vw',
                    overflowY: 'auto',
                  }}
                >
                  <UtilityButton
                    text="Go back"
                    variant="secondary"
                    onClick={() => {
                      setShowCreatePaymentForm(false);
                    }}
                  />
                  <CreatePayment
                    merchantId={merchantId}
                    onSuccess={({ paymentId }) => {
                      void linkPaymentToReport(reportId, paymentId).then(() => {
                        setShowCreatePaymentForm(false);
                        setShowAddPaymentModal(false);
                        refresh();
                      });
                    }}
                  />
                </div>
              ) : (
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing.xxs,
                  }}
                >
                  <List css={{ minWidth: sizing.medium }}>
                    {availablePayments?.map((p) => (
                      <ListItem
                        key={p.uuid}
                        title={
                          <div>
                            <div>
                              Created: {p.createdAt.toLocaleDateString()}{' '}
                              {p.createdAt.toLocaleTimeString()}
                            </div>
                            <div>
                              Paid: {p.paidAt.toLocaleDateString()}{' '}
                              {p.paidAt.toLocaleTimeString()}
                            </div>
                          </div>
                        }
                        subtitle={p.reference}
                        content={p.euroAmount.toString() + '€'}
                        subcontent={
                          p.usdtAmount
                            ? p.usdtAmount.toString() + ' USDT'
                            : p.usdcAmount
                            ? p.usdcAmount.toString() + ' USDC'
                            : ''
                        }
                        action={
                          <UtilityButton
                            variant="icon"
                            icon="plus"
                            onClick={() => linkPayment(p.uuid)}
                            disabled={linkLoading}
                          />
                        }
                      />
                    ))}
                  </List>
                  <UtilityButton
                    text="Create new payment"
                    onClick={() => {
                      setShowCreatePaymentForm(true);
                    }}
                  />
                </div>
              )}
            </Modal>
          )}
        </>
      )}
    </>
  );
};
